'use client';
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter, useSearchParams } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';
import { Eye, EyeOff, ArrowRight, Package, TrendingUp, BarChart3, AlertCircle, CheckCircle2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface LoginForm {
  email: string;
  password: string;
}

interface SignupForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  terms: boolean;
}

const FEATURES = [
  { icon: Package, text: 'Track raw material purchases and auto-calculate cost per unit' },
  { icon: TrendingUp, text: 'Build product recipes with real-time production cost updates' },
  { icon: BarChart3, text: 'Monitor inventory levels and get low-stock alerts instantly' },
];

export default function AuthClient() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const { signIn, signUp, user, loading } = useAuth();

  // Invite link params
  const inviteToken = searchParams.get('token') ?? '';
  const inviteEmail = searchParams.get('email') ?? '';
  const isInviteFlow = !!inviteToken && !!inviteEmail;

  useEffect(() => {
    if (!loading && user) {
      router.replace('/');
    }
  }, [user, loading, router]);

  // Switch to signup tab and pre-fill email when arriving via invite link
  useEffect(() => {
    if (isInviteFlow) {
      setTab('signup');
    }
  }, [isInviteFlow]);

  const {
    register: registerLogin,
    handleSubmit: handleLoginSubmit,
    formState: { errors: loginErrors, isSubmitting: loginSubmitting },
  } = useForm<LoginForm>();

  const {
    register: registerSignup,
    handleSubmit: handleSignupSubmit,
    watch: watchSignup,
    setValue: setSignupValue,
    formState: { errors: signupErrors, isSubmitting: signupSubmitting },
  } = useForm<SignupForm>();

  const signupPassword = watchSignup('password', '');

  // Pre-fill email from invite link
  useEffect(() => {
    if (inviteEmail) {
      setSignupValue('email', inviteEmail);
    }
  }, [inviteEmail, setSignupValue]);

  async function onLogin(data: LoginForm) {
    setAuthError(null);
    try {
      await signIn(data.email, data.password);
      toast.success('Welcome back!');
      router.push('/');
      router.refresh();
    } catch (err: any) {
      const msg = err?.message || 'Sign in failed';
      if (msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('credentials')) {
        setAuthError('Invalid email or password. Please check your credentials and try again.');
      } else if (msg.toLowerCase().includes('email not confirmed')) {
        setAuthError('Please verify your email address before signing in.');
      } else {
        setAuthError(msg);
      }
    }
  }

  async function onSignup(data: SignupForm) {
    setAuthError(null);
    try {
      await signUp(data.email, data.password, {
        fullName: `${data.firstName} ${data.lastName}`.trim(),
        companyName: isInviteFlow ? '' : (data.companyName || `${data.firstName}'s Company`),
        invitationToken: isInviteFlow ? inviteToken : undefined,
      });
      setSignupSuccess(true);
      toast.success('Account created! Signing you in…');
      // Auto sign-in after signup
      try {
        await signIn(data.email, data.password);
        router.push('/');
        router.refresh();
      } catch {
        // If auto sign-in fails, show success and let user sign in manually
      }
    } catch (err: any) {
      const msg = err?.message || 'Account creation failed';
      if (msg.toLowerCase().includes('already registered') || msg.toLowerCase().includes('already exists')) {
        setAuthError('An account with this email already exists. Please sign in instead.');
      } else {
        setAuthError(msg);
      }
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <svg className="animate-spin h-8 w-8 text-primary" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          <p className="text-sm text-muted-foreground">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Brand panel */}
      <div className="hidden lg:flex lg:w-[42%] bg-primary flex-col justify-between p-10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full border-2 border-white" />
          <div className="absolute bottom-20 right-10 w-48 h-48 rounded-full border-2 border-white" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full border border-white" />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <AppLogo size={40} />
            <span className="text-white font-800 text-xl">InventIQ</span>
          </div>
        </div>

        <div className="relative z-10 space-y-6">
          <div>
            <h2 className="text-white text-3xl font-800 leading-tight">
              Smart inventory tracking for modern manufacturers
            </h2>
            <p className="text-white/70 mt-3 text-base leading-relaxed">
              Know your true production costs, never run out of stock, and make pricing decisions with confidence.
            </p>
          </div>

          <div className="space-y-4">
            {FEATURES.map((f, idx) => {
              const FeatureIcon = f.icon;
              return (
                <div key={`feature-${idx}`} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FeatureIcon size={16} className="text-white" />
                  </div>
                  <p className="text-white/85 text-sm leading-relaxed">{f.text}</p>
                </div>
              );
            })}
          </div>

          <div className="border border-white/20 rounded-lg p-4 bg-white/10">
            <p className="text-white/90 text-sm font-600 mb-1">Multi-user workspaces</p>
            <p className="text-white/70 text-xs leading-relaxed">
              Create your company account, then invite team members with custom roles — Admin, Staff, or Viewer. Everyone sees the same live data.
            </p>
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-white/50 text-xs">© 2026 InventIQ · Trusted by manufacturers worldwide</p>
          <div className="flex items-center gap-4 mt-2">
            <Link href="/pricing" className="text-white/50 hover:text-white/80 text-xs transition-colors">
              Pricing
            </Link>
            <span className="text-white/30 text-xs">·</span>
            <Link href="/pricing#faq" className="text-white/50 hover:text-white/80 text-xs transition-colors">
              FAQ
            </Link>
            <span className="text-white/30 text-xs">·</span>
            <Link href="/contact" className="text-white/50 hover:text-white/80 text-xs transition-colors">
              Contact Us
            </Link>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 bg-background overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-800 text-lg text-foreground">InventIQ</span>
          </div>

          {/* Tab switcher */}
          <div className="flex bg-muted rounded-lg p-1 border border-border mb-6">
            <button
              onClick={() => { setTab('login'); setAuthError(null); }}
              className={`flex-1 py-2 text-sm font-600 rounded-md transition-all duration-150 ${tab === 'login' ? 'bg-card shadow-card text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Sign In
            </button>
            <button
              onClick={() => { setTab('signup'); setAuthError(null); }}
              className={`flex-1 py-2 text-sm font-600 rounded-md transition-all duration-150 ${tab === 'signup' ? 'bg-card shadow-card text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Create Account
            </button>
          </div>

          {/* Error banner */}
          {authError && (
            <div className="flex items-start gap-2.5 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{authError}</p>
            </div>
          )}

          {tab === 'login' ? (
            <form onSubmit={handleLoginSubmit(onLogin)} className="space-y-4">
              <div>
                <h1 className="text-2xl font-700 text-foreground">Welcome back</h1>
                <p className="text-sm text-muted-foreground mt-1">Sign in to your InventIQ workspace</p>
              </div>

              <div>
                <label className="block text-sm font-600 text-foreground mb-1">Email Address</label>
                <input
                  type="email"
                  {...registerLogin('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                  })}
                  className={`input-base ${loginErrors.email ? 'input-error' : ''}`}
                  placeholder="you@company.com"
                  autoComplete="email"
                />
                {loginErrors.email && <p className="mt-1 text-xs text-red-500">{loginErrors.email.message}</p>}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-600 text-foreground">Password</label>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...registerLogin('password', {
                      required: 'Password is required',
                      minLength: { value: 6, message: 'Password must be at least 6 characters' },
                    })}
                    className={`input-base pr-10 ${loginErrors.password ? 'input-error' : ''}`}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginErrors.password && <p className="mt-1 text-xs text-red-500">{loginErrors.password.message}</p>}
              </div>

              <button type="submit" disabled={loginSubmitting} className="btn-primary w-full justify-center py-2.5 text-base">
                {loginSubmitting ? (
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                ) : (
                  <>Sign In <ArrowRight size={16} /></>
                )}
              </button>

              <p className="text-center text-sm text-muted-foreground">
                Don&apos;t have an account?{' '}
                <button type="button" onClick={() => { setTab('signup'); setAuthError(null); }} className="text-primary hover:underline font-600">
                  Create one free
                </button>
              </p>
            </form>
          ) : (
            <form onSubmit={handleSignupSubmit(onSignup)} className="space-y-4">
              <div>
                <h1 className="text-2xl font-700 text-foreground">
                  {isInviteFlow ? 'Accept your invitation' : 'Create your account'}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {isInviteFlow
                    ? 'Set up your profile to join the workspace' :'Start tracking your production costs today'}
                </p>
              </div>

              {isInviteFlow && (
                <div className="flex items-start gap-2.5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <Users size={15} className="text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-700">
                    You&apos;ve been invited to join a workspace. Sign up below and you&apos;ll be added automatically.
                  </p>
                </div>
              )}

              {signupSuccess && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle2 size={16} className="text-green-600 flex-shrink-0" />
                  <p className="text-sm text-green-700">Account created! Redirecting you now…</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1">First Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    {...registerSignup('firstName', { required: 'Required' })}
                    className={`input-base ${signupErrors.firstName ? 'input-error' : ''}`}
                    placeholder="Jane"
                  />
                  {signupErrors.firstName && <p className="mt-1 text-xs text-red-500">{signupErrors.firstName.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1">Last Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    {...registerSignup('lastName', { required: 'Required' })}
                    className={`input-base ${signupErrors.lastName ? 'input-error' : ''}`}
                    placeholder="Smith"
                  />
                  {signupErrors.lastName && <p className="mt-1 text-xs text-red-500">{signupErrors.lastName.message}</p>}
                </div>
              </div>

              {!isInviteFlow && (
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1">Company / Business Name</label>
                  <input
                    type="text"
                    {...registerSignup('companyName')}
                    className="input-base"
                    placeholder="Artisan Goods Co."
                  />
                  <p className="mt-1 text-xs text-muted-foreground">This becomes your workspace name. You can change it later.</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-600 text-foreground mb-1">Email Address <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  {...registerSignup('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                  })}
                  className={`input-base ${signupErrors.email ? 'input-error' : ''} ${isInviteFlow ? 'bg-muted cursor-not-allowed' : ''}`}
                  placeholder="you@business.com"
                  readOnly={isInviteFlow}
                />
                {isInviteFlow && (
                  <p className="mt-1 text-xs text-muted-foreground">Email is pre-filled from your invitation and cannot be changed.</p>
                )}
                {signupErrors.email && <p className="mt-1 text-xs text-red-500">{signupErrors.email.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-600 text-foreground mb-1">Password <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...registerSignup('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'At least 8 characters' },
                    })}
                    className={`input-base pr-10 ${signupErrors.password ? 'input-error' : ''}`}
                    placeholder="Min. 8 characters"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupErrors.password && <p className="mt-1 text-xs text-red-500">{signupErrors.password.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-600 text-foreground mb-1">Confirm Password <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...registerSignup('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (v) => v === signupPassword || 'Passwords do not match',
                    })}
                    className={`input-base pr-10 ${signupErrors.confirmPassword ? 'input-error' : ''}`}
                    placeholder="Repeat your password"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupErrors.confirmPassword && <p className="mt-1 text-xs text-red-500">{signupErrors.confirmPassword.message}</p>}
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="terms"
                  {...registerSignup('terms', { required: 'You must accept the terms' })}
                  className="w-4 h-4 accent-primary rounded mt-0.5"
                />
                <label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed">
                  I agree to the{' '}
                  <span className="text-primary font-500">Terms of Service</span>
                  {' '}and{' '}
                  <span className="text-primary font-500">Privacy Policy</span>
                </label>
              </div>
              {signupErrors.terms && <p className="text-xs text-red-500">{signupErrors.terms.message}</p>}

              <button type="submit" disabled={signupSubmitting || signupSuccess} className="btn-primary w-full justify-center py-2.5 text-base">
                {signupSubmitting ? (
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                ) : (
                  <>{isInviteFlow ? 'Join Workspace' : 'Create Account'} <ArrowRight size={16} /></>
                )}
              </button>

              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <button type="button" onClick={() => { setTab('login'); setAuthError(null); }} className="text-primary hover:underline font-600">
                  Sign in
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}